import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import {
  getLocResendDraft,
  previewLocResend,
  resendLOC,
} from '../api/claims';
import type {
  Claim,
  LocResendDraft,
  LocResendFields,
  LocResendPreview,
  LocResendResult,
} from '../api/claims';
import DocViewerDrawer from './DocViewerDrawer';

interface LocResendDialogProps {
  open: boolean;
  claim: Claim | null;
  onClose: () => void;
  onSent: (result: LocResendResult) => void;
}

const fieldLabels: Array<{ key: keyof LocResendFields; label: string }> = [
  { key: 'firstName', label: 'Client first name' },
  { key: 'lastName', label: 'Client surname' },
  { key: 'dateOfBirth', label: 'Date of birth' },
  { key: 'agreementNumber', label: 'Agreement number' },
  { key: 'vehicleMake', label: 'Vehicle make' },
  { key: 'vehicleModel', label: 'Vehicle model' },
  { key: 'lenderReference', label: 'Lender reference' },
  { key: 'addressLine1', label: 'Current address line 1' },
  { key: 'addressLine2', label: 'Current address line 2' },
  { key: 'city', label: 'Current town/city' },
  { key: 'postcode', label: 'Current postcode' },
  { key: 'craAgreementAddressLine1', label: 'Credit report address line 1' },
  { key: 'craAgreementAddressLine2', label: 'Credit report address line 2' },
  { key: 'craAgreementPostcode', label: 'Credit report postcode' },
];

const errorMessage = (error: any, fallback: string) =>
  error?.response?.data?.error || error?.message || fallback;

const newRequestId = () => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, character => {
    const random = Math.floor(Math.random() * 16);
    const value = character === 'x' ? random : (random & 0x3) | 0x8;
    return value.toString(16);
  });
};

const LocResendDialog: React.FC<LocResendDialogProps> = ({
  open,
  claim,
  onClose,
  onSent,
}) => {
  const claimId = claim?.id;
  const [draft, setDraft] = useState<LocResendDraft | null>(null);
  const [fields, setFields] = useState<LocResendFields | null>(null);
  const [preview, setPreview] = useState<LocResendPreview | null>(null);
  const [previewedFields, setPreviewedFields] = useState('');
  const [resendRequestId, setResendRequestId] = useState('');
  const [notifyClient, setNotifyClient] = useState(false);
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [sending, setSending] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open || !claimId) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    setDraft(null);
    setFields(null);
    setPreview(null);
    setPreviewedFields('');
    setResendRequestId('');
    setNotifyClient(false);
    setShowPreview(false);

    getLocResendDraft(claimId)
      .then(result => {
        if (cancelled) return;
        setDraft(result);
        setFields(result.fields);
      })
      .catch(fetchError => {
        if (!cancelled) setError(errorMessage(fetchError, 'Failed to load LOC data'));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [open, claimId]);

  const fieldsFingerprint = useMemo(() => JSON.stringify(fields), [fields]);
  const previewIsCurrent = Boolean(preview && previewedFields === fieldsFingerprint);

  const setField = (key: keyof LocResendFields, value: string | string[]) => {
    setFields(current => current ? { ...current, [key]: value } : current);
  };

  const handlePreview = async () => {
    if (!claim || !draft || !fields) return;
    setGenerating(true);
    setError(null);
    try {
      const result = await previewLocResend(claim.id, draft.sourceVersion, fields);
      setPreview(result);
      setPreviewedFields(JSON.stringify(fields));
      setResendRequestId(newRequestId());
      setShowPreview(true);
    } catch (previewError) {
      setError(errorMessage(previewError, 'Failed to generate LOC preview'));
    } finally {
      setGenerating(false);
    }
  };

  const handleSend = async () => {
    if (!claim || !preview || !previewIsCurrent || !resendRequestId) return;
    setSending(true);
    setError(null);
    try {
      const result = await resendLOC(
        claim.id,
        preview.previewToken,
        notifyClient,
        resendRequestId,
      );
      onSent(result);
      onClose();
    } catch (sendError) {
      setError(errorMessage(sendError, 'Failed to resend LOC'));
      setPreview(null);
      setPreviewedFields('');
      setResendRequestId('');
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      <Dialog open={open} onClose={sending ? undefined : onClose} fullWidth maxWidth="md">
        <DialogTitle>Review and resend LOC</DialogTitle>
        <DialogContent dividers>
          {loading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 5 }}>
              <CircularProgress />
            </Box>
          )}

          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

          {draft && fields && (
            <Stack spacing={2.5}>
              <Alert severity="info">
                This creates a new LOC from the values below. Edits apply to this document only
                and do not change the claim.
              </Alert>

              <Box>
                <Typography variant="subtitle2">Lender recipient</Typography>
                <Typography>{draft.lender.name} — {draft.lender.email}</Typography>
                {draft.previousLoc.sentAt && (
                  <Typography variant="body2" color="text.secondary">
                    Previous LOC sent: {new Date(draft.previousLoc.sentAt).toLocaleString()}
                  </Typography>
                )}
              </Box>

              <Grid container spacing={2}>
                {fieldLabels.map(field => (
                  <Grid size={{ xs: 12, sm: 6 }} key={field.key}>
                    <TextField
                      fullWidth
                      size="small"
                      label={field.label}
                      value={String(fields[field.key] || '')}
                      onChange={event => setField(field.key, event.target.value)}
                      inputProps={{ maxLength: 300 }}
                    />
                  </Grid>
                ))}
                <Grid size={12}>
                  <TextField
                    fullWidth
                    multiline
                    minRows={3}
                    label="Previous addresses (one per line)"
                    value={fields.previousAddresses.join('\n')}
                    onChange={event => setField(
                      'previousAddresses',
                      event.target.value.split('\n').slice(0, 10),
                    )}
                    helperText="Up to 10 addresses"
                  />
                </Grid>
              </Grid>

              <FormControlLabel
                control={(
                  <Checkbox
                    checked={notifyClient}
                    onChange={event => setNotifyClient(event.target.checked)}
                    disabled={!draft.clientEmail}
                  />
                )}
                label={draft.clientEmail
                  ? `Email the client at ${draft.clientEmail} to confirm the LOC was sent`
                  : 'Client notification unavailable — no email address is recorded'}
              />

              {preview && !previewIsCurrent && (
                <Alert severity="warning">
                  The document values changed after the last preview. Generate a new preview before sending.
                </Alert>
              )}
              {previewIsCurrent && (
                <Alert severity="success">
                  Preview ready. Review the document before confirming the resend.
                </Alert>
              )}
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} disabled={sending}>Cancel</Button>
          <Button
            variant="outlined"
            onClick={handlePreview}
            disabled={!fields || loading || generating || sending}
          >
            {generating ? 'Generating…' : previewIsCurrent ? 'Preview again' : 'Generate preview'}
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleSend}
            disabled={!previewIsCurrent || !resendRequestId || sending || generating}
          >
            {sending ? 'Resending…' : 'Confirm and resend LOC'}
          </Button>
        </DialogActions>
      </Dialog>

      <DocViewerDrawer
        open={open && showPreview && Boolean(preview)}
        onClose={() => setShowPreview(false)}
        docUrl={preview?.documentUrl || ''}
        fileName={preview?.blobName || 'LOC preview.docx'}
      />
    </>
  );
};

export default LocResendDialog;
